import { Node, NodeLayer, Tree } from "@/Utils/Collections";
import { getLayer, Layer, LAYER_ORDER } from "@/Utils/hierarchy";

type GetHierarchyOptions = {
  shouldHidePortfolio?: boolean;
};

export type ScenarioNode = {
  uuid: string;
  customer: string;
  brand: string;
  category: string;
  product: string;
};

export type CombinedNode = {
  node: ScenarioNode;
  warchestNode: Node;
};

export type NodeHierarchy = {
  id: number;
  name: string;
  uuid: string;
  label: string;
  combinedNode: CombinedNode | undefined;
  children: NodeHierarchy[];
  shouldHidePortfolio?: boolean;
};

export class Nodes {
  nodeTree: Tree;
  nodes: Map<string, CombinedNode>;
  growthOSToScenario: Map<number, string>;
  shouldHidePortfolio: boolean;

  constructor(nodes: ScenarioNode[], tree: Tree, shouldHidePortfolio = false) {
    this.nodeTree = tree;
    this.nodes = new Map();
    this.growthOSToScenario = new Map();
    this.shouldHidePortfolio = shouldHidePortfolio;

    for (const node of nodes) {
      const warchestNode = this.nodeTree.getNodeByRef(getNodeRef(node));
      if (!warchestNode) {
        throw new Error(`Node with ref ${getNodeRef(node)} not found`);
      }

      this.nodes.set(node.uuid, { node, warchestNode });
      this.growthOSToScenario.set(warchestNode.index, node.uuid);
    }
  }

  getNodes(): CombinedNode[] {
    const layerIndexMap = new Map(LAYER_ORDER.map((layer, index) => [layer, index]));
    const defaultIndex = LAYER_ORDER.length;
    const unorderedNodes = Array.from(this.nodes.values());

    // Precompute layer indexes
    const nodesWithLayerIndex = unorderedNodes.map((node) => ({
      node,
      layerIndex: layerIndexMap.get(getLayer(node.warchestNode.layer)) ?? defaultIndex,
    }));

    nodesWithLayerIndex.sort((a, b) => a.layerIndex - b.layerIndex);

    return nodesWithLayerIndex.map((item) => item.node);
  }

  getNode(uuid: string) {
    return this.nodes.get(uuid);
  }

  getNodeByGrowthOSId(growthOSId: number) {
    const uuid = this.growthOSToScenario.get(growthOSId);
    if (uuid === undefined) {
      throw new Error(`No node found for growthOSId ${growthOSId}`);
    }
    return this.getNode(uuid);
  }

  getAvailableLayers(): NodeLayer[] {
    const layerNodes = this.nodeTree.getAllNodePerLayer({});
    if (!layerNodes) return [];

    return Object.keys(layerNodes) as NodeLayer[];
  }

  getHierarchy(options?: GetHierarchyOptions): NodeHierarchy[] {
    const loop = (childNodeIds: number[]): NodeHierarchy[] => {
      return childNodeIds.reduce((acc: NodeHierarchy[], nodeId): NodeHierarchy[] => {
        const currentNode = this.nodeTree.node[nodeId];
        if (!currentNode) throw new Error(`Node with id ${nodeId} not found`);
        // If currentNode is a default node, directly process its children instead of adding it to the hierarchy
        if (currentNode.name === "default") {
          acc.push(...loop(currentNode.children));
        } else {
          const combinedNode = this.nodes.get(this.growthOSToScenario.get(nodeId) as string);
          if (!combinedNode) throw new Error(`No node found for growthOSId ${nodeId}`);
          const uuid = combinedNode.node.uuid;
          const label =
            combinedNode.warchestNode.extras.mappedName || combinedNode.warchestNode.name;

          const hierarchyNode: NodeHierarchy = {
            id: nodeId,
            name: currentNode.name,
            children: loop(currentNode.children),
            label,
            uuid,
            combinedNode,
          };
          acc.push(hierarchyNode);
        }
        return acc;
      }, []);
    };

    const firstNodeId = 0;
    const firstNode = this.nodeTree.node[firstNodeId];

    if (!firstNode || !firstNode.children) {
      return [];
    }

    if (
      this.shouldHidePortfolio ||
      // Only exclude true portfolio node
      (options?.shouldHidePortfolio && this.getAvailableLayers().includes(Layer.Brand))
    ) {
      return loop(firstNode.children);
    }

    return loop([firstNodeId]);
  }

  getDefaultNode(options?: GetHierarchyOptions) {
    return this.getHierarchy(options)[0]?.combinedNode;
  }

  findNodeFromHierarchy = (hierarchy: NodeHierarchy[], uuid: string): NodeHierarchy | undefined => {
    for (const node of hierarchy) {
      if (node.uuid === uuid) {
        return node;
      }
      const childNode = this.findNodeFromHierarchy(node.children, uuid);
      if (childNode) {
        return childNode;
      }
    }
    return undefined;
  };

  getNonDefaultNode(node: Node) {
    return this.nodeTree.getNonDefaultNode(node);
  }

  getNodeAndAncestors(nodeUUID: string): CombinedNode[] {
    const parentNodes: CombinedNode[] = [];
    const node = this.getNode(nodeUUID);
    if (!node) {
      return [];
    }

    let currentNode = node;
    parentNodes.push(currentNode);

    while (currentNode.warchestNode.parent) {
      const parentNode = this.nodeTree.getNonDefaultParentNode(currentNode.warchestNode);
      if (!parentNode) {
        break;
      }
      const parentNodeCombined = this.getNodeByGrowthOSId(parentNode.index);
      if (!parentNodeCombined) {
        break;
      }
      parentNodes.push(parentNodeCombined);
      currentNode = parentNodeCombined;
    }
    return parentNodes;
  }

  getNodesInSameLayerAndParent(nodeUUID: string, excludeSelf: boolean = false): CombinedNode[] {
    const node = this.getNode(nodeUUID);
    if (!node) {
      return [];
    }

    const currentLayer = getLayer(node.warchestNode.layer);
    const parentNode = this.nodeTree.getNonDefaultParentNode(node.warchestNode);

    if (!parentNode) {
      return []; // No parent found
    }

    const loop = (childNodeIds: number[]): CombinedNode[] => {
      return childNodeIds.reduce((acc: CombinedNode[], nodeId): CombinedNode[] => {
        const currentNode = this.nodeTree.node[nodeId];
        if (!currentNode) return acc;

        // If currentNode is a default node, process its children
        if (currentNode.name === "default") {
          acc.push(...loop(currentNode.children));
        } else {
          const combinedNode = this.nodes.get(this.growthOSToScenario.get(nodeId) as string);
          if (!combinedNode) return acc;

          const nodeLayer = getLayer(combinedNode.warchestNode.layer);
          const nodeParent = this.nodeTree.getNonDefaultParentNode(combinedNode.warchestNode);

          // Only include nodes in the same layer and with the same parent
          if (nodeLayer === currentLayer && nodeParent?.index === parentNode.index) {
            if (!excludeSelf || combinedNode.node.uuid !== nodeUUID) {
              acc.push(combinedNode);
            }
          }

          // Process children
          acc.push(...loop(currentNode.children));
        }
        return acc;
      }, []);
    };

    return loop([parentNode.index]);
  }

  getNodeLayer(nodeUUID: string): Layer | undefined {
    const node = this.getNode(nodeUUID);
    if (!node) {
      return undefined;
    }
    return getLayer(node.warchestNode.layer);
  }

  findNodeByBrandCategoryProduct(
    brand: string,
    category: string,
    product: string
  ): CombinedNode | undefined {
    const allNodes = Array.from(this.nodes.values());

    return allNodes.find((combinedNode) => {
      const node = combinedNode.node;
      return node.brand === brand && node.category === category && node.product === product;
    });
  }
}

const getNodeRef = (node: ScenarioNode): string[] => {
  const metaRef = [node.customer, node.brand, node.category, node.product];

  let lastDefaultIndex = metaRef.length;

  for (let i = metaRef.length - 1; i >= 0; i--) {
    if (metaRef[i] !== "default") {
      lastDefaultIndex = i + 1;
      break;
    }
  }

  return metaRef.slice(0, lastDefaultIndex);
};

export const trimArray = (arr: string[], trimValue: string): string[] => {
  let lastIndex = arr.length - 1;

  // Start from the end of the array and look for the trimValue
  while (lastIndex >= 0 && arr[lastIndex] === trimValue) {
    lastIndex--;
  }

  // Slice the array to exclude the trimmed values
  return arr.slice(0, lastIndex + 1);
};

export const getChildren = (nodes: Nodes, node: Node): string[] => {
  const children = [] as number[];

  getNonDefaultNodes(nodes, node, children);

  const mappedChildren: string[] = [];

  children.forEach((child) => {
    const node = nodes.getNodeByGrowthOSId(child);

    if (!node) {
      return;
    }

    mappedChildren.push(node.node.uuid);
  });

  return mappedChildren;
};

const getNonDefaultNodes = (nodes: Nodes, node: Node, children: number[] = []) => {
  if (node.name !== "default") {
    children.push(node.index);
  }

  if (node.children.length > 0) {
    node.children.forEach((child) => {
      getNonDefaultNodes(nodes, nodes.nodeTree.getNodeById(child), children);
    });
  }
};
